import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { SquaresFour as Squares, Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { listSpaces, createSpace, updateSpace, deleteSpace } from '../services/api';
import { TERRITOIRES } from '../constants/countries';
import { COLLEGES } from '../constants/colleges';

export default function SpacesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await listSpaces(); setItems(r.data.items || []); }
    catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet espace ?')) return;
    await deleteSpace(id); toast.success('Espace supprimé'); load();
  };

  return (
    <div className="p-6 space-y-4" data-testid="spaces-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Squares size={22} className="text-[#2D5A4C]" /> Espaces partagés
          </h1>
          <p className="text-sm text-[#5E6D66]">Audiences réutilisables (territoires + collèges + organisations) que vous pouvez rappeler depuis n'importe quelle publication.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="new-space-btn"><Plus size={16} className="mr-1" /> Nouvel espace</Button>
      </div>

      {loading && <Loader2 className="animate-spin" />}
      {!loading && items.length === 0 && (
        <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">Aucun espace pour l'instant.</CardContent></Card>
      )}

      <div className="grid gap-2">
        {items.map((s) => (
          <Card key={s.id} className="border-[#E8E6E1]" data-testid={`space-card-${s.id}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-3 h-10 rounded" style={{ backgroundColor: s.color || '#2D5A4C' }} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1A2421]">{s.name}</p>
                <p className="text-xs text-[#5E6D66] line-clamp-1">{s.description}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {(s.audience?.territoires || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                  {(s.audience?.colleges || []).map((c) => <Badge key={c} variant="outline" className="text-[10px]">Collège {c}</Badge>)}
                  {(s.target_roles || []).map((r) => <Badge key={r} className="bg-[#2D5A4C]/10 text-[#2D5A4C] text-[10px]">{r}</Badge>)}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(s)} data-testid={`edit-space-${s.id}`}><PencilSimple size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(s.id)} data-testid={`delete-space-${s.id}`}><Trash size={14} className="text-red-600" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && <SpaceDialog space={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SpaceDialog({ space, onClose, onSaved }) {
  const isEdit = !!space.id;
  const [name, setName] = useState(space.name || '');
  const [description, setDescription] = useState(space.description || '');
  const [territoires, setTerritoires] = useState(space.audience?.territoires || []);
  const [colleges, setColleges] = useState(space.audience?.colleges || []);
  const [targetRoles, setTargetRoles] = useState(space.target_roles || ['membre_externe']);
  const [color, setColor] = useState(space.color || '#2D5A4C');
  const [saving, setSaving] = useState(false);

  const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, territoires, colleges, target_roles: targetRoles, color };
      if (isEdit) await updateSpace(space.id, payload);
      else await createSpace(payload);
      toast.success('Espace enregistré');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="space-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Modifier' : 'Nouvel'} espace</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nom</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="space-name" /></div>
          <div><Label className="text-xs">Description</Label><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="space-description" /></div>
          <div>
            <Label className="text-xs">Territoires</Label>
            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-[#E8E6E1] rounded p-2">
              {TERRITOIRES.map((t) => (
                <label key={t.value} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={territoires.includes(t.value)} onChange={() => toggle(territoires, setTerritoires, t.value)} />
                  {t.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Collèges</Label>
            <div className="flex flex-wrap gap-2">
              {COLLEGES.map((c) => (
                <label key={c.value} className="flex items-center gap-1 text-xs px-2 py-1 border border-[#E8E6E1] rounded">
                  <input type="checkbox" checked={colleges.includes(c.value)} onChange={() => toggle(colleges, setColleges, c.value)} />
                  {c.value}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Rôles cibles</Label>
            <div className="flex gap-2">
              {['membre_externe', 'partenaire'].map((r) => (
                <label key={r} className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={targetRoles.includes(r)} onChange={() => toggle(targetRoles, setTargetRoles, r)} />{r}
                </label>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Couleur</Label><Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-20" data-testid="space-color" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="space-save">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
